import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribe — Raj Shamani", key: "title" },
      {
        name: "description",
        content: "Manage your email preferences and unsubscribe from Raj Shamani emails.",
        key: "description",
      },
      { name: "robots", content: "noindex, nofollow", key: "robots" },
      { property: "og:title", content: "Unsubscribe — Raj Shamani", key: "og:title" },
      {
        property: "og:description",
        content: "Manage your email preferences and unsubscribe from Raj Shamani emails.",
        key: "og:description",
      },
      { property: "og:type", content: "website", key: "og:type" },
      { name: "twitter:card", content: "summary", key: "twitter:card" },
    ],
  }),
  component: Unsubscribe,
});

type State = "loading" | "valid" | "already" | "invalid" | "done" | "error";

function Unsubscribe() {
  const [state, setState] = useState<State>("loading");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token");
    setToken(t);
    if (!t) {
      setState("invalid");
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.valid) setState("valid");
        else if (d?.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      })
      .catch(() => setState("error"));
  }, []);

  async function confirm() {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const d = await res.json();
      if (d?.success) setState("done");
      else if (d?.reason === "already_unsubscribed") setState("already");
      else setState("error");
    } catch {
      setState("error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-xl px-6 py-32 text-center">
      <div className="text-xs tracking-widest uppercase text-gold mb-4">Email preferences</div>
      <h1 className="text-4xl font-bold mb-6">Unsubscribe</h1>

      {state === "loading" && <p className="text-muted-foreground">Checking your link…</p>}

      {state === "valid" && (
        <>
          <p className="text-muted-foreground mb-8">
            Confirm below and we'll stop sending you emails from this site.
          </p>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="px-8 py-4 rounded-md bg-gold text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50 shadow-gold"
          >
            {busy ? "Unsubscribing…" : "Confirm unsubscribe"}
          </button>
        </>
      )}

      {state === "done" && (
        <p className="text-muted-foreground">
          You're unsubscribed. You won't receive further emails from us.
        </p>
      )}

      {state === "already" && (
        <p className="text-muted-foreground">You're already unsubscribed. Nothing more to do.</p>
      )}

      {state === "invalid" && (
        <p className="text-muted-foreground">
          This unsubscribe link is invalid or has expired.
        </p>
      )}

      {state === "error" && (
        <p className="text-muted-foreground">Something went wrong. Please try again later.</p>
      )}
    </div>
  );
}
